/**
 * Ikaros embroidery cart guard.
 *
 * WHY THIS EXISTS - order #6963
 * A customer checked out with one line: Embroidery - Front, single colour,
 * KD 4, and no cap. The stitching brief was on the order; the cap was not,
 * and the uploaded logo went with it. The cap had to be added by hand
 * afterwards, leaving the order part-paid.
 *
 * Embroidery is priced as its own hidden product because Liquid cannot add
 * money to a line item. That means an embroidery line is a real, separate
 * line the customer can delete - or that can be created alone if the cap
 * add fails after the add-on add succeeded.
 *
 * This script is the backstop. It runs on every page, watches the cart, and
 * deletes any embroidery line that has no cap to belong to.
 *
 * HOW LINES ARE PAIRED
 * At add time the studio stamps the same _emb_group on the cap line and on
 * every add-on line created with it. Underscore-prefixed properties are
 * hidden from the customer but present in the cart JSON, which is all this
 * needs. Two caps in one cart therefore stay distinguishable.
 *
 * Lines from before the group key existed have no _emb_group. Those are
 * only removed when the cart holds no cap at all - the exact #6963 shape -
 * because with nothing to pair against, guessing would be worse.
 *
 * THE ADD-IN-PROGRESS FLAG - do not remove
 * A legitimate add is two requests: the add-ons go in first, the cap a
 * moment later. Between them the cart genuinely holds an embroidery line
 * with no cap, which is indistinguishable from the state this guard exists
 * to clean up. The first build of this file deleted the add-on mid-add and
 * every embroidery order silently became a plain cap - the opposite of the
 * bug it was written for, and worse, because the customer would have been
 * charged for a cap and expected stitching.
 *
 * So the studio raises window.__ikarosEmbAdding for the length of an add
 * and the sweep stands down while it is set. The studio does its own
 * rollback if the cap never lands, and this guard picks up anything it
 * misses on the next cart read.
 *
 * WHAT THIS CANNOT DO
 * Theme code cannot reach Shopify's checkout. A customer who edits the cart
 * through /cart/change.js directly, or who is already inside checkout when
 * the cart changes, is not covered. Closing that needs a Cart and Checkout
 * Validation Function, which is a Shopify app, not a theme file.
 */

(() => {
  const EMB_TYPE = 'Embroidery';
  const GROUP = '_emb_group';

  let running = false;
  let lastSignature = '';

  const isEmb = (item) => (item.product_type || '') === EMB_TYPE;
  const groupOf = (item) => (item.properties && item.properties[GROUP]) || null;
  const addInFlight = () => !!window.__ikarosEmbAdding;

  /* Which lines should not be there. Returns line numbers (1-based), which
     is what /cart/change.js expects, highest first so that removing one
     does not shift the next. */
  function orphans(items) {
    const capGroups = new Set();
    let capCount = 0;

    items.forEach((item) => {
      if (isEmb(item)) return;
      capCount += 1;
      const g = groupOf(item);
      if (g) capGroups.add(g);
    });

    const doomed = [];
    items.forEach((item, i) => {
      if (!isEmb(item)) return;
      const g = groupOf(item);
      if (g) {
        // Paired line: keep only while its cap is still in the cart.
        if (!capGroups.has(g)) doomed.push(i + 1);
      } else if (capCount === 0) {
        // Unpaired legacy line with nothing it could belong to.
        doomed.push(i + 1);
      }
    });
    return doomed.reverse();
  }

  async function sweep() {
    if (running || addInFlight()) return;
    running = true;
    try {
      const cart = await (await fetch('/cart.js', { headers: { Accept: 'application/json' } })).json();
      if (!cart || !cart.items || !cart.items.length) {
        lastSignature = '';
        return;
      }

      // Skip the work when nothing has changed since the last look.
      const sig = cart.items.map((i) => i.key + ':' + i.quantity).join('|');
      if (sig === lastSignature) return;
      lastSignature = sig;

      const doomed = orphans(cart.items);
      if (!doomed.length) return;

      // Re-check: an add may have started while the cart was being read.
      if (addInFlight()) {
        lastSignature = '';
        return;
      }

      for (const line of doomed) {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity: 0 }),
        });
      }

      lastSignature = '';
      notify(doomed.length);

      // Only the cart page shows stale line items after this. Elsewhere the
      // drawer re-reads the cart on open, so a reload would be an
      // unexplained page jump in the middle of browsing.
      const onCartPage = /\/cart(\/|$|\?)/.test(window.location.pathname + window.location.search);
      if (onCartPage && document.body.dataset.embSweepReload !== 'done') {
        document.body.dataset.embSweepReload = 'done';
        window.location.reload();
      }
    } catch (e) {
      console.warn('[ikaros-embroidery-guard]', e);
    } finally {
      running = false;
    }
  }

  function notify(count) {
    try {
      sessionStorage.setItem(
        'ikaros-emb-removed',
        count === 1
          ? 'Embroidery needs a cap, so it was removed from your cart.'
          : 'Embroidery needs a cap, so those lines were removed from your cart.'
      );
    } catch (e) {
      /* private mode - the removal still happened, only the message is lost */
    }
  }

  function showNotice() {
    let msg = null;
    try {
      msg = sessionStorage.getItem('ikaros-emb-removed');
      if (msg) sessionStorage.removeItem('ikaros-emb-removed');
    } catch (e) {
      return;
    }
    if (!msg) return;

    const host =
      document.querySelector('cart-items-component') ||
      document.querySelector('.cart-items') ||
      document.querySelector('#MainContent');
    if (!host) return;

    const p = document.createElement('p');
    p.className = 'emb-guard-notice';
    p.setAttribute('role', 'status');
    p.textContent = msg;
    host.prepend(p);
  }

  function start() {
    showNotice();
    sweep();

    // Horizon re-renders the cart through section morphing rather than a
    // page load, so there is no single event to hang this on. Watching for
    // cart fetches catches every path: drawer, cart page, quick add.
    const origFetch = window.fetch;
    window.fetch = function (...args) {
      const url = String((args[0] && args[0].url) || args[0] || '');
      const p = origFetch.apply(this, args);
      if (/\/cart\/(add|change|update|clear)/.test(url)) {
        // Long enough for a studio add to raise its flag and for Horizon to
        // finish its own add before the cart is judged.
        p.then(() => setTimeout(sweep, 1200)).catch(() => {});
      }
      return p;
    };

    document.addEventListener('cart:updated', () => setTimeout(sweep, 1200));
    document.addEventListener('cart:refresh', () => setTimeout(sweep, 1200));
    window.addEventListener('pageshow', () => setTimeout(sweep, 600));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
